import path from 'path';
import fs from 'fs-extra';
import { WorkflowConfig, WorkflowState, Task, AnalysisResult, TaskState, TaskHistoryEntry, TaskActionResult, NextTaskRecommendation, TaskPriority, TaskStatus } from '../types';
import { WorkflowRunner } from './workflow-runner';
import { TaskAnalyzer } from './task-analyzer';
import { TaskWatcher } from './task-watcher';
import { logger } from '../utils/logger';
import { loadConfig, saveConfig } from '../utils/config';
import { WorkflowTemplates } from '../templates';
import { WORKFLOW_DIR, TODO_FILE_PATHS } from '../constants';
// import { ClaudeIntegration } from './claude-integration'; // 已移至 @gongfu/claude-integration
import { SimpleCache } from '../utils/cache';
import { EnhancedTaskAnalyzer, EnhancedAnalysisResult } from './enhanced-task-analyzer';
// import { ContextBuilder } from './context/context-builder'; // Imported but not used directly
import { KnowledgeBase } from './knowledge-base';
import { TaskSplitter, SplitOptions, SplitResult, SubTask } from './task-splitter';
import { TaskTemplateManager } from './task-template';
import { TaskStore } from './task-store';
import { DesignDocManager } from './design-doc-manager';
import { PRDGenerator } from './prd-generator';
import { HookManager, HookRegistry, HookType } from './hooks';

export interface WorkflowManagerOptions {
  projectRoot?: string;
  configPath?: string;
  enableAI?: boolean;
}

export class WorkflowManager {
  private projectRoot: string;
  private workflowDir: string;
  private config: WorkflowConfig | null = null;
  private runner: WorkflowRunner;
  private analyzer: TaskAnalyzer;
  private watcher: TaskWatcher | null = null;
  private cache: SimpleCache;
  private taskStore: TaskStore;
  private designDocManager: DesignDocManager;
  private prdGenerator: PRDGenerator;
  private hookManager: HookManager;
  private hookRegistry: HookRegistry;

  constructor(options: WorkflowManagerOptions = {}) {
    this.projectRoot = options.projectRoot || process.cwd();
    this.workflowDir = path.join(this.projectRoot, WORKFLOW_DIR);
    
    this.runner = new WorkflowRunner(this.projectRoot);
    this.analyzer = new TaskAnalyzer({
      enableAI: options.enableAI || false
    });
    this.cache = new SimpleCache({
      cacheDir: path.join(this.workflowDir, '.cache'),
      ttl: 600000 // 10 minutes
    });
    
    // 先创建 HookManager
    this.hookManager = new HookManager(this.projectRoot);
    this.hookRegistry = new HookRegistry(this.hookManager, this.projectRoot);
    
    // 创建其他管理器，传入 HookManager
    this.taskStore = new TaskStore(this.projectRoot, this.hookManager);
    this.designDocManager = new DesignDocManager(this.projectRoot);
    this.prdGenerator = new PRDGenerator(this.projectRoot);
  }

  async initialize(force = false): Promise<void> {
    if (await this.isInitialized() && !force) {
      throw new Error('Workflow system already initialized. Use --force to reinitialize.');
    }

    logger.info('Initializing workflow system...');
    
    // Clean up deprecated .gongfu directory if it exists
    const deprecatedGongfuDir = path.join(this.projectRoot, '.gongfu');
    if (await fs.pathExists(deprecatedGongfuDir)) {
      logger.warn('Found deprecated .gongfu directory, removing it...');
      try {
        await fs.remove(deprecatedGongfuDir);
        logger.info('Removed deprecated .gongfu directory');
      } catch (error) {
        logger.error('Failed to remove .gongfu directory:', error);
      }
    }

    // Create directory structure
    const dirs = [
      'commands',
      'workflows',
      'knowledge/patterns',
      'knowledge/decisions',
      'knowledge/troubleshooting',
      'logs'
    ];

    for (const dir of dirs) {
      await fs.ensureDir(path.join(this.workflowDir, dir));
    }

    // Create default configuration
    const config: WorkflowConfig = {
      version: '1.0.0',
      project: {
        name: path.basename(this.projectRoot),
        type: await this.detectProjectType(),
        framework: await this.detectFrameworks(),
        language: await this.detectLanguage()
      },
      settings: {
        autoSync: true,
        syncInterval: 300000, // 5 minutes
        enableAI: false,
        checkMode: 'quick'
      }
    };

    await saveConfig(this.workflowDir, config);
    await this.createDefaultTemplates();

    // Initialize task store to ensure tasks.json exists
    await this.taskStore.initialize();
    logger.info('Task store initialized');
    
    // Create PRD and Design template directories
    await fs.ensureDir(path.join(this.workflowDir, 'templates', 'prd'));
    await fs.ensureDir(path.join(this.workflowDir, 'templates', 'design'));
    await fs.ensureDir(path.join(this.workflowDir, 'docs', 'prd'));
    await fs.ensureDir(path.join(this.workflowDir, 'docs', 'design'));
    logger.info('Document directories initialized');
    
    // Initialize hooks system
    await this.hookManager.initialize();
    await this.hookRegistry.registerBuiltinHooks();
    await this.hookRegistry.scanUserHooks(path.join(this.workflowDir, 'hooks'));
    logger.info('Hooks system initialized');

    // Initialize Claude Code integration if available
    // TODO: 使用 @gongfu/claude-integration 包
    // const claudeIntegration = new ClaudeIntegration({
    //   projectRoot: this.projectRoot,
    //   force: force,
    //   enableHooks: true,
    //   safeMode: true
    // });

    // if (await claudeIntegration.isClaudeProject() || process.env.CLAUDE_CODE) {
    //   logger.info('Detected Claude Code environment, setting up integration...');
    //   await claudeIntegration.initialize();
    //   await claudeIntegration.createWorkflowSymlink();
    //   logger.success('Claude Code integration configured successfully!');
    // }

    logger.success('Workflow system initialized successfully!');
  }

  async isInitialized(): Promise<boolean> {
    return fs.pathExists(this.workflowDir);
  }

  async isClaudeIntegrated(): Promise<boolean> {
    const claudeDir = path.join(this.projectRoot, '.claude');
    const hasClaudeDir = await fs.pathExists(claudeDir);
    const hasWorkflowCommands = await fs.pathExists(path.join(claudeDir, 'commands', 'task.md'));
    return hasClaudeDir && hasWorkflowCommands;
  }

  async loadConfig(): Promise<WorkflowConfig> {
    if (!this.config) {
      // Try cache first
      const cached = await this.cache.get<WorkflowConfig>('config');
      if (cached) {
        this.config = cached;
        return cached;
      }

      // Load from disk
      this.config = await loadConfig(this.workflowDir);
      
      // Cache it
      await this.cache.set('config', this.config);
    }
    return this.config;
  }

  async runCommand(commandName: string): Promise<void> {
    await this.ensureInitialized();
    return this.runner.runCommand(commandName);
  }

  async startWorkflow(type: string, options: { name?: string; description?: string } = {}): Promise<void> {
    await this.ensureInitialized();
    return this.runner.startWorkflow(type, options);
  }

  async getWorkflowStatus(): Promise<WorkflowState | null> {
    await this.ensureInitialized();
    return this.runner.getStatus();
  }

  async analyzeTasks(): Promise<AnalysisResult> {
    await this.ensureInitialized();
    
    // 使用 TaskStore 获取任务
    const tasks = await this.taskStore.getTasks();
    
    // 直接使用 tasks 数组进行分析
    const statistics = {
      total: tasks.length,
      completed: tasks.filter(t => t.status === 'done' || t.status === 'archived').length,
      inProgress: tasks.filter(t => t.status === 'in_progress').length,
      pending: tasks.filter(t => t.status === 'todo').length,
      completionRate: tasks.length > 0 ? Math.round((tasks.filter(t => t.status === 'done' || t.status === 'archived').length / tasks.length) * 100) : 0
    };
    
    return {
      tasks,
      statistics,
      recommendations: [],
      insights: {
        trends: [],
        bottlenecks: [],
        suggestions: []
      },
      patterns: []
    };
  }

  async startWatching(): Promise<void> {
    await this.ensureInitialized();
    
    if (this.watcher) {
      logger.warn('Task watcher is already running');
      return;
    }

    const todoPath = await this.findTodoFile();
    if (!todoPath) {
      throw new Error('No TODO file found to watch');
    }

    this.watcher = new TaskWatcher({
      projectRoot: this.projectRoot,
      todoPath,
      workflowDir: this.workflowDir
    });

    await this.watcher.start();
  }

  async stopWatching(): Promise<void> {
    if (this.watcher) {
      await this.watcher.stop();
      this.watcher = null;
    }
  }

  async syncTasks(): Promise<void> {
    await this.ensureInitialized();
    
    // 直接返回，因为我们不再从 TODO.md 读取
    logger.info('Tasks are now managed directly in .workflow/tasks.json');
    logger.info('TODO.md is automatically exported when tasks are updated');
  }

  private async ensureInitialized(): Promise<void> {
    if (!await this.isInitialized()) {
      throw new Error('Workflow system not initialized. Run "workflow init" first.');
    }
  }

  private async findTodoFile(): Promise<string | null> {
    for (const todoPath of TODO_FILE_PATHS) {
      const fullPath = path.join(this.projectRoot, todoPath);
      if (await fs.pathExists(fullPath)) {
        return fullPath;
      }
    }

    return null;
  }

  private async detectProjectType(): Promise<string> {
    const packageJsonPath = path.join(this.projectRoot, 'package.json');
    
    if (await fs.pathExists(packageJsonPath)) {
      const pkg = await fs.readJson(packageJsonPath);
      
      if (pkg.workspaces) return 'monorepo';
      if (pkg.dependencies?.next) return 'nextjs-app';
      if (pkg.dependencies?.react) return 'react-app';
      if (pkg.dependencies?.vue) return 'vue-app';
      if (pkg.dependencies?.express) return 'node-app';
      
      return 'node-package';
    }
    
    if (await fs.pathExists(path.join(this.projectRoot, 'Cargo.toml'))) {
      return 'rust-project';
    }
    
    if (await fs.pathExists(path.join(this.projectRoot, 'go.mod'))) {
      return 'go-project';
    }
    
    return 'unknown';
  }

  private async detectFrameworks(): Promise<string[]> {
    const frameworks: string[] = [];
    const packageJsonPath = path.join(this.projectRoot, 'package.json');
    
    if (await fs.pathExists(packageJsonPath)) {
      const pkg = await fs.readJson(packageJsonPath);
      const deps = { ...pkg.dependencies, ...pkg.devDependencies };
      
      if (deps.react) frameworks.push('react');
      if (deps.vue) frameworks.push('vue');
      if (deps.angular) frameworks.push('angular');
      if (deps.next) frameworks.push('nextjs');
      if (deps.express) frameworks.push('express');
      if (deps.fastify) frameworks.push('fastify');
      if (deps.typescript) frameworks.push('typescript');
    }
    
    return frameworks;
  }

  private async detectLanguage(): Promise<string> {
    if (await fs.pathExists(path.join(this.projectRoot, 'tsconfig.json'))) {
      return 'typescript';
    }
    if (await fs.pathExists(path.join(this.projectRoot, 'package.json'))) {
      return 'javascript';
    }
    if (await fs.pathExists(path.join(this.projectRoot, 'Cargo.toml'))) {
      return 'rust';
    }
    if (await fs.pathExists(path.join(this.projectRoot, 'go.mod'))) {
      return 'go';
    }
    if (await fs.pathExists(path.join(this.projectRoot, 'requirements.txt'))) {
      return 'python';
    }
    
    return 'unknown';
  }

  private async createDefaultTemplates(): Promise<void> {
    const projectName = path.basename(this.projectRoot);
    
    // Create default command templates
    const commands = {
      'init.md': WorkflowTemplates.getInitCommand({ projectName, projectRoot: this.projectRoot }),
      'todo.md': WorkflowTemplates.getTodoCommand(),
      'context.md': WorkflowTemplates.getContextCommand(projectName),
      'review.md': WorkflowTemplates.getReviewCommand()
    };

    for (const [filename, content] of Object.entries(commands)) {
      await fs.writeFile(
        path.join(this.workflowDir, 'commands', filename),
        content
      );
    }

    // Create default workflow templates
    const workflows = {
      'feature.yaml': WorkflowTemplates.getFeatureWorkflow(),
      'bugfix.yaml': WorkflowTemplates.getBugfixWorkflow(),
      'refactor.yaml': WorkflowTemplates.getRefactorWorkflow()
    };

    for (const [filename, content] of Object.entries(workflows)) {
      await fs.writeFile(
        path.join(this.workflowDir, 'workflows', filename),
        content
      );
    }
  }

  // Task state management for hooks integration - now uses TaskStore

  async getTaskState(): Promise<TaskState | null> {
    try {
      // Get current task from TaskStore
      await this.taskStore.initialize();
      const currentTask = await this.taskStore.getCurrentTask();
      
      if (!currentTask) {
        return null;
      }

      // Convert to TaskState format for backward compatibility
      // Convert to TaskState format for backward compatibility
      const taskState: TaskState = {
        current: {
          id: currentTask.id,
          task: currentTask.content,
          phase: (currentTask.phase || 'understanding') as TaskState['current']['phase'],
          startTime: currentTask.startedAt || currentTask.createdAt,
          priority: currentTask.priority,
          context: currentTask.notes ? { notes: currentTask.notes } : undefined
        },
        history: [], // History is now managed in tasks.json
        lastUpdate: currentTask.updatedAt
      };
      return taskState;
    } catch (error) {
      logger.error('Failed to get task state:', error);
      return null;
    }
  }

  async startTask(taskInfo: {
    task: string;
    priority: 'high' | 'medium' | 'low';
    phase?: TaskState['current']['phase'];
    context?: Record<string, any>;
  }): Promise<TaskActionResult> {
    try {
      // Initialize TaskStore
      await this.taskStore.initialize();

      // Create new task and add to TaskStore
      const newTask = await this.taskStore.addTask({
        content: taskInfo.task,
        priority: taskInfo.priority,
        status: 'in_progress',
        isCurrent: true,
        phase: taskInfo.phase || 'understanding',
        startedAt: new Date(),
        notes: taskInfo.context ? JSON.stringify(taskInfo.context) : undefined
      });

      // Set the new task as current
      await this.taskStore.setCurrentTask(newTask.id);
      
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  async completeCurrentTask(notes?: string): Promise<TaskActionResult> {
    try {
      // Initialize TaskStore
      await this.taskStore.initialize();

      // Get current task from TaskStore
      const currentTask = await this.taskStore.getCurrentTask();
      
      if (!currentTask) {
        return { success: false, error: 'No active task to complete' };
      }

      // Update task status to completed
      await this.taskStore.updateTask(currentTask.id, {
        status: 'done',
        isCurrent: false,
        completedAt: new Date(),
        notes: notes ? (currentTask.notes ? currentTask.notes + '\n' + notes : notes) : currentTask.notes
      });

      // Create history entry for backward compatibility
      const historyEntry: TaskHistoryEntry = {
        task: currentTask.content,
        status: 'completed',
        startTime: currentTask.startedAt || currentTask.createdAt,
        endTime: new Date(),
        duration: Date.now() - new Date(currentTask.startedAt || currentTask.createdAt).getTime(),
        notes,
        phase: currentTask.phase || 'understanding'
      };
      
      return { success: true, data: historyEntry };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  async recommendNextTask(): Promise<NextTaskRecommendation[] | null> {
    try {
      // 使用 TaskStore 获取任务
      const tasks = await this.taskStore.getTasks();
      
      // 过滤出未完成的高优先级任务
      const activeTasks = tasks
        .filter(t => ['todo', 'in_progress', 'paused', 'blocked', 'review'].includes(t.status))
        .sort((a, b) => {
          // 状态权重
          const statusWeight: Record<string, number> = { 'in_progress': 5, 'blocked': 4, 'review': 3, 'paused': 2, 'todo': 1 };
          const aStatusWeight = statusWeight[a.status] || 0;
          const bStatusWeight = statusWeight[b.status] || 0;
          
          // 如果状态权重相同，按优先级排序
          if (aStatusWeight === bStatusWeight) {
            const priorityWeight = { urgent: 4, high: 3, medium: 2, low: 1 };
            return (priorityWeight[b.priority] || 2) - (priorityWeight[a.priority] || 2);
          }
          
          return bStatusWeight - aStatusWeight;
        })
        .slice(0, 5); // 只返回前5个

      if (activeTasks.length === 0) {
        return null;
      }

      // 转换为推荐格式
      const nextTasks: NextTaskRecommendation[] = activeTasks.map(task => ({
        task: task.content,
        priority: task.priority,
        reason: this.getTaskRecommendationReason(task.status),
        estimatedDuration: this.estimateTaskDuration(task.content)
      }));

      return nextTasks;
    } catch (error) {
      logger.error('Failed to get task recommendations:', error);
      return null;
    }
  }

  private getTaskRecommendationReason(status: TaskStatus): string {
    const reasonMap: Record<string, string> = {
      'in_progress': '正在进行中的任务',
      'blocked': '被阻塞的任务，需要解决依赖',
      'review': '待评审的任务',
      'paused': '暂停的任务，可以继续',
      'todo': '待处理的新任务'
    };
    return reasonMap[status] || '待处理任务';
  }

  private estimateTaskDuration(task: string): number {
    // Simple heuristic for task duration estimation (in minutes)
    const keywords = {
      'simple': 30,
      'fix': 60,
      'implement': 120,
      'refactor': 180,
      'design': 240,
      'research': 120,
      'test': 90,
      'document': 60
    };

    const lowerTask = task.toLowerCase();
    for (const [keyword, duration] of Object.entries(keywords)) {
      if (lowerTask.includes(keyword)) {
        return duration;
      }
    }
    
    return 60; // Default 1 hour
  }

  private async syncTaskCompletion(taskDescription: string): Promise<void> {
    try {
      // 使用 TaskStore 更新任务状态
      const tasks = await this.taskStore.getTasks();
      const task = tasks.find(t => t.content.includes(taskDescription));
      
      if (task) {
        await this.taskStore.updateTask(task.id, {
          status: 'done' as TaskStatus,
          completedAt: new Date()
        });
      }
    } catch (error) {
      logger.warn('Failed to sync task completion:', error);
    }
  }

  async estimateTaskTime(taskDescription: string, priority: string = 'medium'): Promise<EnhancedAnalysisResult | null> {
    try {
      // Initialize analyzers if not already done
      if (!this.enhancedAnalyzer) {
        // Create simple mock implementations to avoid circular dependencies
        const mockContextEngine = {
          build: async () => ({
            metadata: {},
            task: null,
            history: [],
            knowledge: [],
            tools: [],
            workflow: null,
            totalSize: 0,
            timestamp: new Date(),
            items: [] // Add items array for generatePrompt
          })
        };
        
        const knowledgeBase = new KnowledgeBase(path.join(this.workflowDir, 'knowledge'));
        
        this.enhancedAnalyzer = new EnhancedTaskAnalyzer(
          mockContextEngine as any,
          knowledgeBase,
          { enableAI: false }
        );
      }

      // Create a task object for analysis
      const task: Task = {
        id: Date.now().toString(),
        content: taskDescription,
        priority: priority as TaskPriority,
        status: 'todo' as TaskStatus,
        position: 0
      };

      // Analyze the task
      const result = await this.enhancedAnalyzer.analyze(task);
      return result;
    } catch (error) {
      logger.error('Failed to estimate task time:', error);
      return null;
    }
  }

  private enhancedAnalyzer?: EnhancedTaskAnalyzer;
  private taskSplitter?: TaskSplitter;
  // private templateManager?: TaskTemplateManager; // Declared but not used

  // Document management methods
  async generatePRD(idea: string, options?: any): Promise<string> {
    return this.prdGenerator.generateFromIdea(idea, options);
  }
  
  async generateDesignDoc(prdPath: string, options?: any): Promise<{ doc: any; content: string }> {
    return this.designDocManager.generateFromPRD(prdPath, options);
  }
  
  async listDesignDocs(): Promise<any[]> {
    return this.designDocManager.listDesignDocs();
  }
  
  async getDesignDoc(id: string): Promise<{ doc: any; content: string } | null> {
    return this.designDocManager.getDesignDoc(id);
  }
  
  // Hook system access
  getHookManager(): HookManager {
    return this.hookManager;
  }
  
  getHookRegistry(): HookRegistry {
    return this.hookRegistry;
  }

  async splitTask(taskDescription: string, options: SplitOptions = {}): Promise<SplitResult | null> {
    try {
      // Initialize splitter if not already done
      if (!this.taskSplitter) {
        // Initialize enhanced analyzer first
        if (!this.enhancedAnalyzer) {
          const mockContextEngine = {
            build: async () => ({
              metadata: {},
              task: null,
              history: [],
              knowledge: [],
              tools: [],
              workflow: null,
              totalSize: 0,
              timestamp: new Date(),
              items: []
            })
          };
          
          const knowledgeBase = new KnowledgeBase(path.join(this.workflowDir, 'knowledge'));
          
          this.enhancedAnalyzer = new EnhancedTaskAnalyzer(
            mockContextEngine as any,
            knowledgeBase,
            { enableAI: false }
          );
        }
        
        this.taskSplitter = new TaskSplitter(this.enhancedAnalyzer);
      }

      // Split the task
      const result = await this.taskSplitter.split(taskDescription, options);
      return result;
    } catch (error) {
      logger.error('Failed to split task:', error);
      return null;
    }
  }

  calculateStartTime(taskId: string, subtasks: SubTask[]): number {
    // Build dependency graph and calculate earliest start time
    const earliestStart = new Map<string, number>();
    const times = new Map<string, number>();
    const deps = new Map<string, string[]>();
    
    // Initialize maps
    for (const task of subtasks) {
      times.set(task.id, task.estimatedTime);
      deps.set(task.id, task.dependencies || []);
    }
    
    // Recursive function to calculate earliest start
    const calculate = (id: string): number => {
      if (earliestStart.has(id)) {
        return earliestStart.get(id)!;
      }
      
      const taskDeps = deps.get(id) || [];
      if (taskDeps.length === 0) {
        earliestStart.set(id, 0);
        return 0;
      }
      
      let maxEnd = 0;
      for (const dep of taskDeps) {
        const depStart = calculate(dep);
        const depDuration = times.get(dep) || 0;
        maxEnd = Math.max(maxEnd, depStart + depDuration);
      }
      
      earliestStart.set(id, maxEnd);
      return maxEnd;
    };
    
    return calculate(taskId);
  }

  async saveSubtasks(splitResult: SplitResult): Promise<boolean> {
    try {
      // Create subtasks directory if not exists
      const subtasksDir = path.join(this.workflowDir, 'subtasks');
      await fs.ensureDir(subtasksDir);
      
      // Generate unique ID for this split
      const splitId = `split-${Date.now()}`;
      const filename = `${splitId}.json`;
      
      // Save the split result
      await fs.writeJson(path.join(subtasksDir, filename), {
        ...splitResult,
        createdAt: new Date(),
        splitId
      }, { spaces: 2 });
      
      // Update the current task with split information
      await this.taskStore.initialize();
      const currentTask = await this.taskStore.getCurrentTask();
      if (currentTask) {
        await this.taskStore.updateTask(currentTask.id, {
          notes: `${currentTask.notes || ''}\nSplit into ${splitResult.taskCount} subtasks (${splitId})`.trim()
        });
      }
      
      logger.info(`Saved ${splitResult.taskCount} subtasks to ${filename}`);
      return true;
    } catch (error) {
      logger.error('Failed to save subtasks:', error);
      return false;
    }
  }
}